import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useGoogleLogin } from '@react-oauth/google';
import { GoogleIcon } from '../components/GoogleIcon';
import { identifyAirbridgeUser, trackSignin } from '../utils/airbridge';
import { getDefaultRoute } from '../utils/defaultRoute';
import { persistAuthSession } from '../utils/authStorage';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USE_CUSTOM_OAUTH = String(process.env.REACT_APP_USE_CUSTOM_GOOGLE_OAUTH || '').toLowerCase() === 'true';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
// Fixed production OAuth callback origin — prevents redirect_uri_mismatch when the Capacitor
// server URL differs from the registered Google callback URI across APK builds.
// Falls back to window.location.origin so the preview / web flow still works.
const OAUTH_CALLBACK_ORIGIN = process.env.REACT_APP_OAUTH_CALLBACK_ORIGIN || '';

/**
 * Dedicated Google sign-in page for the Capacitor WebView deep-link flow.
 *
 * TWO modes:
 *
 * 1. webview_flow=1 (Capacitor WebView path)
 *    - WebView navigated here via window.location.href after popup failed.
 *    - Auto-redirects to auth.emergentagent.com (EXTERNAL domain).
 *    - Capacitor intercepts auth.emergentagent.com → fires Intent → Chrome opens.
 *    - WebView stays on this page and polls via visibilitychange when app returns.
 *    - After Chrome OAuth, GoogleAuthCallback parks token → /app-login-complete → allurexp://auth.
 *    - App comes to foreground → visibilitychange fires → poll finds token → login.
 *
 * 2. No webview_flow (Chrome browser path, opened externally or for direct use)
 *    - Shows a Google Sign-In button (popup-based via @react-oauth/google).
 *    - After auth, parks JWT via /api/auth/park-app-token so the app picks it up.
 *    - Navigates to /app-login-complete.
 */
export default function AppGoogleLoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const capSession = searchParams.get('cap_session');
  const fromWebViewFlow = searchParams.get('webview_flow') === '1';
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState('idle'); // idle | opening | parking | done | error

  const pollRef = React.useRef(null);

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  React.useEffect(() => () => stopPolling(), [stopPolling]);

  // ─── Path 1: WebView flow ─────────────────────────────────────────────────
  // Auto-redirect to Google OAuth. When the app has a custom Google OAuth client,
  // navigate directly to accounts.google.com (shows "Continue to allurexp.com").
  // Otherwise fall back to auth.emergentagent.com proxy.
  // Capacitor fires an Intent for these external domains so Chrome opens.
  // The WebView stays on this page and polls on visibilitychange when the user returns.
  React.useEffect(() => {
    if (!fromWebViewFlow || !capSession || status !== 'idle') return;
    // Short delay so the page can render a loading state first
    const timer = setTimeout(() => {
      setStatus('opening');

      if (USE_CUSTOM_OAUTH && GOOGLE_CLIENT_ID) {
        // Direct Google OAuth — shows "Continue to <allurexp.com>" on the account picker.
        // NOTE: response_type=token does NOT accept `nonce` (nonce is only for id_token flows).
        const redirectUri = `${OAUTH_CALLBACK_ORIGIN || window.location.origin}/auth/google/callback`;
        const params = new URLSearchParams({
          client_id: GOOGLE_CLIENT_ID,
          redirect_uri: redirectUri,
          response_type: 'token',
          scope: 'openid email profile',
          // Pass cap_session through state so GoogleAuthCallback can park the token
          state: `cap=${encodeURIComponent(capSession)}`,
          include_granted_scopes: 'true',
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
      } else {
        // Fallback: Emergent-managed OAuth proxy (shows "Continue to emergentagent.com")
        const redirectUrl = `${window.location.origin}/auth/google/callback?cap_session=${encodeURIComponent(capSession)}`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [fromWebViewFlow, capSession, status]);

  // Poll for the parked token when the app returns to foreground (after Chrome OAuth)
  React.useEffect(() => {
    if (!fromWebViewFlow || !capSession) return;

    const tryFetch = async () => {
      try {
        const res = await axios.get(`${API}/auth/poll-app-token?session_id=${capSession}`);
        if (res.data?.access_token) {
          stopPolling();
          // Persist to BOTH localStorage AND native Preferences and set
          // the fresh-login marker so UnlockPage skips the biometric
          // prompt right after this OAuth round-trip — the user JUST
          // authenticated in the browser, no need to re-gate them.
          await persistAuthSession(res.data.access_token, res.data.user);
          onLogin?.(res.data.user);
          navigate(getDefaultRoute(res.data.user), { replace: true });
        }
      } catch (err) {
        if (err.response?.status === 410) {
          stopPolling();
          setStatus('error');
        }
        // 404 = not yet parked — keep waiting
      }
    };

    const onForeground = () => {
      if (document.hidden) return;
      // App came back to foreground — start/continue polling
      tryFetch();
      if (!pollRef.current) {
        pollRef.current = setInterval(tryFetch, 2000);
        // Auto-stop after 60 seconds to avoid indefinite polling
        setTimeout(() => stopPolling(), 60000);
      }
    };

    document.addEventListener('visibilitychange', onForeground);
    window.addEventListener('focus', onForeground);

    return () => {
      document.removeEventListener('visibilitychange', onForeground);
      window.removeEventListener('focus', onForeground);
      stopPolling();
    };
  }, [fromWebViewFlow, capSession, navigate, onLogin, stopPolling]);

  // ─── Path 2: Chrome browser flow ─────────────────────────────────────────
  // If already logged in in this browser, park the existing token immediately.
  React.useEffect(() => {
    if (fromWebViewFlow || !capSession) return;
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      setStatus('parking');
      axios.post(`${API}/auth/park-app-token`, {
        session_id: capSession,
        access_token: token,
      }).then(() => {
        navigate('/app-login-complete', { replace: true });
      }).catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setStatus('idle');
      });
    }
  }, [fromWebViewFlow, capSession, navigate]);

  const afterAuth = React.useCallback(async (accessToken, user) => {
    if (!capSession) {
      // Non-Capacitor / web fallback path — persist to both storages so
      // the session survives a browser cache clear the same way a
      // native update does.
      await persistAuthSession(accessToken, user);
      onLogin?.(user);
      navigate('/', { replace: true });
      return;
    }
    setStatus('parking');
    try {
      await axios.post(`${API}/auth/park-app-token`, {
        session_id: capSession,
        access_token: accessToken,
      });
      navigate('/app-login-complete', { replace: true });
    } catch (err) {
      toast.error('Could not link sign-in to app. Please try again.');
      setStatus('error');
    }
  }, [capSession, navigate, onLogin]);

  const loginCustom = useGoogleLogin({
    flow: 'implicit',
    scope: 'openid email profile',
    onSuccess: async (tokenResponse) => {
      try {
        const res = await axios.post(`${API}/auth/google/oauth-token`, {
          access_token: tokenResponse.access_token,
        });
        if (res.data.needs_profile) {
          if (capSession) sessionStorage.setItem('cap_session', capSession);
          navigate('/google/complete', {
            state: {
              signupToken: res.data.signup_token,
              email: res.data.email,
              suggestedFirstName: res.data.suggested_first_name || '',
              suggestedLastName: res.data.suggested_last_name || '',
              capSession,
            },
          });
          return;
        }
        identifyAirbridgeUser(res.data.user);
        trackSignin({ user: res.data.user, method: 'google' });
        await afterAuth(res.data.access_token, res.data.user);
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Google sign-in failed.');
        setStatus('error');
      } finally {
        setBusy(false);
      }
    },
    onError: () => {
      setBusy(false);
      toast.error('Google sign-in was cancelled.');
      setStatus('error');
    },
  });

  // ─── Render: WebView waiting state ───────────────────────────────────────
  if (fromWebViewFlow) {
    return (
      <div
        data-testid="app-google-login-page"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a0a0a',
          padding: 24,
        }}
      >
        <img
          src="/allure-logo.png"
          alt="Allure"
          style={{ width: 72, height: 72, objectFit: 'contain', marginBottom: 32 }}
          className="animate-pulse"
        />
        <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
          Signing in with Google…
        </h1>
        <p style={{ color: '#888', fontSize: 14, textAlign: 'center', lineHeight: 1.6, maxWidth: 280 }}>
          {status === 'error'
            ? 'Session expired. Please go back and try again.'
            : 'A browser window is opening. Complete sign-in there, then return to the app.'}
        </p>
        {status === 'error' && (
          <button
            data-testid="app-google-login-retry"
            onClick={() => navigate('/login', { replace: true })}
            style={{
              marginTop: 24,
              background: 'rgba(255,255,255,0.08)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 12,
              padding: '12px 28px',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Back to Login
          </button>
        )}
      </div>
    );
  }

  // ─── Render: Chrome browser flow (standard sign-in page) ─────────────────
  if (status === 'parking') {
    return (
      <div
        data-testid="app-google-login-parking"
        style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a' }}
      >
        <img src="/allure-logo.png" alt="Allure" style={{ width: 72, height: 72, objectFit: 'contain' }} className="animate-pulse" />
        <p style={{ color: '#888', fontSize: 15, marginTop: 24 }}>Sending you back to the app…</p>
      </div>
    );
  }

  return (
    <div
      data-testid="app-google-login-page"
      className="app-shell"
      style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a', padding: 24 }}
    >
      <img src="/allure-logo.png" alt="Allure" style={{ width: 72, height: 72, objectFit: 'contain', marginBottom: 32 }} />
      <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>Sign in with Google</h1>
      <p style={{ color: '#888', fontSize: 14, textAlign: 'center', marginBottom: 32, lineHeight: 1.6 }}>
        Complete sign-in here, then return to the Allure app.
      </p>

      {USE_CUSTOM_OAUTH && process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
        <button
          type="button"
          data-testid="app-google-login-button"
          disabled={busy || status === 'parking'}
          onClick={() => { setBusy(true); loginCustom(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', color: '#222', border: 'none', borderRadius: 12, padding: '14px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%', maxWidth: 320, justifyContent: 'center' }}
        >
          <GoogleIcon size={20} />
          {busy ? 'Opening Google…' : 'Continue with Google'}
        </button>
      ) : (
        <p style={{ color: '#e53e3e', fontSize: 14 }}>
          Google sign-in is not configured. Please sign in via the app settings.
        </p>
      )}

      {status === 'error' && (
        <p data-testid="app-google-login-error" style={{ color: '#e53e3e', fontSize: 13, marginTop: 16 }}>
          Something went wrong. Please try again.
        </p>
      )}
    </div>
  );
}
